import { useState } from "react";
import Lottie from "lottie-react";
import animationData from "@/assets/landing-hero.lottie.json";
import { getTenantConfig } from "@/lib/tenant";

export type LandingHeroLottieProps = {
  alt: string;
};

export function LandingHeroLottie({ alt }: LandingHeroLottieProps) {
  const tenant = getTenantConfig();
  const isMindfulMe = tenant.brandName === "MindfulMe";

  const [reducedMotion] = useState<boolean>(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );

  if (isMindfulMe) {
    return (
      <div
        role="img"
        aria-label={alt}
        className="w-full max-w-[460px] max-md:max-w-[320px] aspect-square flex items-center justify-center relative select-none"
      >
        {!reducedMotion && (
          <style>{`
            @keyframes mm-bob-man {
              0%,100%{ transform:translateY(0); }
              50%    { transform:translateY(-7px); }
            }
            @keyframes mm-bob-woman {
              0%,100%{ transform:translateY(0); }
              50%    { transform:translateY(-9px); }
            }
            @keyframes mm-fh1-anim {
              0%   { transform:translateY(0)    scale(1);   opacity:.9; }
              45%  { transform:translateY(-20px) scale(1.1); opacity:1; }
              100% { transform:translateY(-42px) scale(.8); opacity:0; }
            }
            @keyframes mm-fh2-anim {
              0%   { transform:translateY(0)    scale(1);    opacity:.75; }
              45%  { transform:translateY(-18px) scale(1.1); opacity:.9; }
              100% { transform:translateY(-38px) scale(.75); opacity:0; }
            }
            @keyframes mm-fh3-anim {
              0%   { transform:translateY(0)    scale(1);    opacity:.65; }
              45%  { transform:translateY(-16px) scale(1.08);opacity:.85;}
              100% { transform:translateY(-34px) scale(.7);  opacity:0; }
            }
            @keyframes mm-bubble {
              0%,100%{ transform:scale(1);    }
              50%    { transform:scale(1.07); }
            }
            @keyframes mm-twinkle {
              0%,100%{ opacity:0; transform:scale(.4); }
              50%    { opacity:1; transform:scale(1);  }
            }
            .mm-man   { transform-origin:140px 320px; animation:mm-bob-man   3.4s ease-in-out infinite; }
            .mm-woman { transform-origin:340px 325px; animation:mm-bob-woman 2.9s ease-in-out infinite .5s; }
            .mm-fh1   { transform-origin: 84px 115px; animation:mm-fh1-anim  3.8s ease-in-out infinite 0s; }
            .mm-fh2   { transform-origin:240px  60px; animation:mm-fh2-anim  3.8s ease-in-out infinite 1.3s; }
            .mm-fh3   { transform-origin:392px  84px; animation:mm-fh3-anim  3.8s ease-in-out infinite 2.4s; }
            .mm-sp    { transform-origin:390px  84px; animation:mm-bubble    2.6s ease-in-out infinite; }
            .mm-tw1   { animation:mm-twinkle 2.8s ease-in-out infinite 0s;   }
            .mm-tw2   { animation:mm-twinkle 2.8s ease-in-out infinite .9s; }
            .mm-tw3   { animation:mm-twinkle 2.8s ease-in-out infinite 1.8s; }
          `}</style>
        )}

        <svg
          viewBox="0 0 480 450"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
        >
          {/* bg circle */}
          <circle cx="240" cy="226" r="190" fill="#EEF1FF" />
          <circle cx="240" cy="226" r="190" fill="none" stroke="#C7D2FE" strokeWidth="1.5" opacity="0.5" />

          {/* twinkling stars */}
          <g className="mm-tw1"><path d="M70 146 L74 135 L78 146 L66 140 L82 140Z" fill="#818CF8" opacity=".7" /></g>
          <g className="mm-tw2"><path d="M410 154 L414 144 L418 154 L406 149 L422 149Z" fill="#FB7185" opacity=".65" /></g>
          <g className="mm-tw3"><path d="M234 54 L237 45 L240 54 L231 49 L243 49Z" fill="#A78BFA" opacity=".6" /></g>

          {/* floating hearts */}
          <g className="mm-fh1">
            <path d="M84 127 C84 116,68 116,68 127 C68 138,84 147,84 152 C84 147,100 138,100 127 C100 116,84 116,84 127Z" fill="#818CF8"/>
          </g>
          <g className="mm-fh2">
            <path d="M240 72 C240 64,228 64,228 72 C228 80,240 88,240 92 C240 88,252 80,252 72 C252 64,240 64,240 72Z" fill="#FB7185" opacity=".8"/>
          </g>
          <g className="mm-fh3">
            <path d="M392 96 C392 86,377 86,377 96 C377 106,392 114,392 119 C392 114,407 106,407 96 C407 86,392 86,392 96Z" fill="#FF5B37" opacity=".75"/>
          </g>

          {/* speech bubble */}
          <g className="mm-sp">
            <rect x="326" y="42" width="124" height="72" rx="18" fill="#D9F99D"/>
            <path d="M350 114 L338 134 L374 114Z" fill="#D9F99D"/>
            <path d="M388 68 C388 58,372 58,372 68 C372 78,388 87,388 92 C388 87,404 78,404 68 C404 58,388 58,388 68Z" fill="#FF5B37"/>
          </g>

          <g className="mm-man">

            <path
              d="
                M 14 422 L 14 400
                C 14 368, 20 336, 44 312
                C 58 300, 78 284, 106 268
                C 112 264, 118 260, 118 254
                L 164 254
                C 164 260, 170 264, 176 268
                C 204 284, 224 300, 238 312
                C 262 336, 268 368, 268 400
                L 268 422
                C 268 443, 224 450, 140 450
                C 58  450, 14  443, 14  422 Z
              "
              fill="#4F46E5"
            />

            {/* arm crease — left */}
            <path d="M 38 393 L 38 430" stroke="#3730A3" strokeWidth="5" strokeLinecap="round"/>
            {/* arm crease — right */}
            <path d="M 244 393 L 244 430" stroke="#3730A3" strokeWidth="5" strokeLinecap="round"/>

            {/* collar V */}
            <path d="M132 256 L140 278 L148 256" stroke="#312E81" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>

            {/* ── NECK ──
               Top edge is an SVG arc identical to the bottom of the head circle
               (cx=140 cy=174 r=64, sweep=0 = counterclockwise = bottom arc).
               This means zero visible seam between head and neck.
               At y=236, head width ≈ 30px → that is the narrow top of the trapezoid.
               Neck widens to collar width (118→164) at y=254 below. */}
            <path
              d="M 125 236 A 64 64 0 0 0 155 236 L 164 254 L 118 254 Z"
              fill="#F5A98A"
            />

            {/* ── ears (behind head) ── */}
            <ellipse cx="78"  cy="177" rx="13" ry="15" fill="#F5A98A"/>
            <ellipse cx="202" cy="177" rx="13" ry="15" fill="#F5A98A"/>
            <ellipse cx="78"  cy="177" rx="7"  ry="9"  fill="#D0784E" opacity=".2"/>
            <ellipse cx="202" cy="177" rx="7"  ry="9"  fill="#D0784E" opacity=".2"/>

            {/* ── head ── */}
            <circle cx="140" cy="174" r="64" fill="#F5A98A"/>

            <path
              d="
                M 77 162
                A 64 64 0 0 1 203 162
                C 192 150, 168 144, 152 142
                C 140 141, 128 143, 112 148
                C 96 153, 82 159, 77 162 Z
              "
              fill="#1E1B18"
            />

            {/* eyebrows */}
            <path d="M112 168 Q124 161 136 166" stroke="#1E1B18" strokeWidth="3" strokeLinecap="round" fill="none"/>
            <path d="M144 166 Q156 161 168 168" stroke="#1E1B18" strokeWidth="3" strokeLinecap="round" fill="none"/>

            {/* eyes */}
            <circle cx="122" cy="183" r="10.5" fill="#1E1B18"/>
            <circle cx="158" cy="183" r="10.5" fill="#1E1B18"/>
            <circle cx="126" cy="179" r="4"    fill="white"/>
            <circle cx="162" cy="179" r="4"    fill="white"/>

            {/* smile */}
            <path d="M124 208 Q140 225 156 208" stroke="#1E1B18" strokeWidth="5" strokeLinecap="round" fill="none"/>
          </g>

          <g className="mm-woman">

            {/* ── shirt / torso blob ── */}
            <path
              d="
                M 212 422 L 212 400
                C 212 368, 218 336, 244 312
                C 258 300, 278 284, 304 268
                C 310 264, 316 260, 316 256
                L 366 256
                C 366 260, 372 264, 378 268
                C 404 284, 422 300, 436 312
                C 460 336, 466 368, 466 400
                L 466 422
                C 466 443, 420 450, 338 450
                C 258 450, 212 443, 212 422 Z
              "
              fill="#FF5B37"
            />

            {/* arm crease — left */}
            <path d="M 236 393 L 236 430" stroke="#C2410C" strokeWidth="5" strokeLinecap="round"/>
            {/* arm crease — right */}
            <path d="M 444 393 L 444 430" stroke="#C2410C" strokeWidth="5" strokeLinecap="round"/>

            {/* collar V */}
            <path d="M330 258 L340 280 L350 258" stroke="#C2410C" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>

            {/* ── NECK ──
               Same arc approach: top uses arc of head circle (cx=340 cy=193 r=59,
               sweep=0 counterclockwise = bottom portion). At y=250, head ≈ 28px wide.
               Trapezoid widens from 28px at top to collar width 316→366 at y=256. */}
            <path
              d="M 326 250 A 59 59 0 0 0 354 250 L 366 256 L 316 256 Z"
              fill="#F9BEA6"
            />

            {/* ── ears ── */}
            <ellipse cx="283" cy="196" rx="12" ry="14" fill="#F9BEA6"/>
            <ellipse cx="397" cy="196" rx="12" ry="14" fill="#F9BEA6"/>
            <ellipse cx="283" cy="196" rx="6.5" ry="8"  fill="#D0784E" opacity=".18"/>
            <ellipse cx="397" cy="196" rx="6.5" ry="8"  fill="#D0784E" opacity=".18"/>

            {/* ── head ── */}
            <circle cx="340" cy="193" r="59" fill="#F9BEA6"/>

            <path
              d="
                M 281 193
                A 59 59 0 0 1 399 193
                C 390 178, 373 163, 357 158
                C 344 154, 330 155, 317 160
                C 303 166, 289 179, 281 193 Z
              "
              fill="#1E1B18"
            />

            <ellipse cx="340" cy="136" rx="32" ry="14" fill="#1E1B18"/>
            <ellipse cx="340" cy="106" rx="28" ry="25" fill="#1E1B18"/>
            <ellipse cx="330" cy="97" rx="10" ry="7" fill="#3A3530" opacity=".4" transform="rotate(-20 330 97)"/>

            {/* subtle rosy cheeks */}
            <ellipse cx="308" cy="213" rx="15" ry="10" fill="#FF8C78" opacity=".22"/>
            <ellipse cx="372" cy="213" rx="15" ry="10" fill="#FF8C78" opacity=".22"/>

            {/* eyes — open round circles like Kidology photo 2 */}
            <circle cx="316" cy="201" r="9"   fill="#1E1B18"/>
            <circle cx="364" cy="201" r="9"   fill="#1E1B18"/>
            <circle cx="319" cy="198" r="3.5" fill="white"/>
            <circle cx="367" cy="198" r="3.5" fill="white"/>

            {/* smile — small & gentle, photo-2 style */}
            <path d="M320 222 Q340 234 360 222" stroke="#1E1B18" strokeWidth="4" strokeLinecap="round" fill="none"/>
          </g>


        </svg>
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={alt}
      className="w-full max-w-[460px] max-md:max-w-[320px] aspect-square"
    >
      <Lottie
        animationData={animationData}
        loop={!reducedMotion}
        autoplay={!reducedMotion}
        aria-hidden="true"
      />
    </div>
  );
}
